import { useEffect, useState } from 'react';
import type { RepairServiceType } from '@techgitter/contracts';
import { fetchRepairServiceTypes } from '../services/repairServiceTypesApi';

// Module-level cache for the active-list (the hot path used by the quote
// picker). The catalog rarely changes during a session and is consumed
// from multiple components — sharing one fetch is worth the bookkeeping.
//
// Mutations on the admin page must call `invalidateRepairServiceTypesCache()`
// after a successful create / update / delete so live consumers refresh
// without a page reload. The admin page itself bypasses this cache
// (it calls `fetchRepairServiceTypes(signal, { includeInactive })` for the
// full list) so we only ever cache the active-only variant here.
let cache: RepairServiceType[] | null = null;
let inflight: Promise<RepairServiceType[]> | null = null;
// Subscribers are notified after `invalidate()` so currently-mounted
// hooks re-fetch immediately. Without this, a tech adding a service via
// the admin tab would have to refresh / re-mount the quote view to see
// the new entry.
const subscribers = new Set<() => void>();

const getCatalog = (): Promise<RepairServiceType[]> => {
  // Use `!== null` rather than truthiness — an empty-array cache is a valid
  // result (the catalog might genuinely have no active rows) and must not
  // be re-fetched on every consumer mount.
  if (cache !== null) return Promise.resolve(cache);
  if (!inflight) {
    // Deliberately don't accept a per-consumer AbortSignal here: the shared
    // request lives across all subscribers, and tying its lifetime to the
    // first caller meant any quick unmount (HMR / parent re-render that
    // remounts the picker) would abort the request for *everyone* else
    // awaiting the same promise. Consumers that unmount simply discard
    // their setState callback via their own AbortController.
    inflight = fetchRepairServiceTypes()
      .then((items) => {
        cache = items;
        return items;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/**
 * Discard the cached active-list and notify all live `useRepairServiceTypes`
 * consumers so they re-fetch. Call this after any successful mutation
 * (create / update / delete) on the admin page.
 */
export function invalidateRepairServiceTypesCache(): void {
  cache = null;
  inflight = null;
  subscribers.forEach((notify) => {
    try {
      notify();
    } catch {
      // a subscriber's own state-setter shouldn't bring down the others
    }
  });
}

export interface UseRepairServiceTypesResult {
  types: RepairServiceType[];
  isLoading: boolean;
  error: string | null;
}

export function useRepairServiceTypes(): UseRepairServiceTypesResult {
  const [types, setTypes] = useState<RepairServiceType[]>(cache ?? []);
  const [isLoading, setIsLoading] = useState(cache == null);
  const [error, setError] = useState<string | null>(null);
  // A revision counter that bumps each time invalidate() fires; it forces
  // the load effect below to re-run so this hook sees fresh data.
  const [rev, setRev] = useState(0);

  useEffect(() => {
    const onInvalidate = () => setRev((r) => r + 1);
    subscribers.add(onInvalidate);
    return () => {
      subscribers.delete(onInvalidate);
    };
  }, []);

  useEffect(() => {
    // After an invalidate, `cache` is null again and we need to refetch.
    // We deliberately don't short-circuit on `cache` truthiness here
    // because the cache may have been refilled since the last render —
    // setTypes is idempotent on identical refs.
    let cancelled = false;
    setIsLoading(cache === null);
    getCatalog()
      .then((items) => {
        if (cancelled) return;
        setTypes(items);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rev]);

  return { types, isLoading, error };
}
